// frontend/src/components/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import logo from '../assets/logo.svg';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Home, User, ArrowRightLeft, Banknote, FileText,
  Package, MessageCircle, LogOut, UserPlus, HelpCircle,
  Contact, BarChart3, TestTube, Shield, BookOpen, Sparkles
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [goldPrice, setGoldPrice] = useState(null);
  const [displayPrice, setDisplayPrice] = useState(null);
  const [priceError, setPriceError] = useState('');
  const [priceColor, setPriceColor] = useState('text-gray-800');
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const prevPriceRef = useRef(null);
  const colorTimeoutRef = useRef(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const socketRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // --- Click Outside ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // --- Socket ---
  useEffect(() => {
    if (isLoggedIn && token) {
      socketRef.current = io(API_URL, { auth: { token }, transports: ['websocket'] });

      socketRef.current.on('connect', () => {
        if (!isAdmin) socketRef.current.emit('join_user_room');
      });

      socketRef.current.on('user_receive_message', () => {
        if (!window.location.pathname.includes('/chat_page')) {
          setHasNewMessage(true);
          setUnreadCount(prev => prev + 1);
        }
      });

      socketRef.current.on('admin_unread_updated', ({ total_unread }) => {
        setUnreadCount(total_unread);
        if (total_unread > 0 && !window.location.pathname.includes('/admin-chat_page')) {
          setHasNewMessage(true);
        }
      });

      const handleAdminJoin = () => {
        if (isAdmin) {
          setHasNewMessage(false);
          setUnreadCount(0);
        }
      };
      window.addEventListener('admin_joined_room', handleAdminJoin);

      return () => {
        window.removeEventListener('admin_joined_room', handleAdminJoin);
        socketRef.current?.disconnect();
      };
    }
  }, [isLoggedIn, token, isAdmin]);

  // --- Reset Badge ---
  useEffect(() => {
    if (window.location.pathname.includes('/chat_page') || window.location.pathname.includes('/admin-chat_page')) {
      setHasNewMessage(false);
    }
  }, [window.location.pathname]);

  // --- Fetch User & Price ---
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const res = await api.get('/api/users/me');
          setIsLoggedIn(true);
          setIsAdmin(res.data.user.is_admin === 1);
        } catch (err) {
          localStorage.removeItem('authToken');
          setIsLoggedIn(false);
        }
      }
    };

    const fetchGoldPrice = async () => {
      try {
        const res = await api.get('/api/prices/prices');
        const newPrice = res.data.price;
        setPriceError('');
        if (goldPrice !== null && newPrice !== goldPrice) {
          if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
          setPriceColor(newPrice > goldPrice ? 'text-emerald-600' : 'text-rose-600');
          colorTimeoutRef.current = setTimeout(() => setPriceColor('text-gray-800'), 3000);
        }
        setGoldPrice(newPrice);
      } catch (err) {
        setPriceError('خطا');
      }
    };

    fetchUser();
    fetchGoldPrice();
    const interval = setInterval(fetchGoldPrice, 5000);
    return () => {
      clearInterval(interval);
      if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
    };
  }, [token, goldPrice]);

  // --- Animate Price ---
  useEffect(() => {
    if (goldPrice === null || goldPrice === displayPrice) return;
    const targetPrice = goldPrice;
    let currentPrice = displayPrice !== null ? displayPrice : goldPrice;
    const difference = targetPrice - currentPrice;
    const duration = 800;
    const startTime = performance.now();

    const animatePrice = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const newDisplayPrice = Math.round(currentPrice + difference * easedProgress);
      setDisplayPrice(newDisplayPrice);
      if (progress < 1) requestAnimationFrame(animatePrice);
      else setDisplayPrice(targetPrice);
    };
    requestAnimationFrame(animatePrice);
  }, [goldPrice, displayPrice]);

  // --- Logout ---
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setOpen(false);
    navigate('/login_page');
  };

  const closeMenu = () => setOpen(false);

  // --- منوی زیبا و مرتب ---
  const renderMenuItems = () => {
    const items = [];

    // --- عمومی ---
    items.push(
      <NavItem key="home" icon={<Home />} label="خانه" link="/" onClick={closeMenu} />,
      <NavItem key="chart" icon={<BarChart3 />} label="نمودار قیمت" link="/price-chart" onClick={closeMenu} />,
      <NavItem key="labs" icon={<TestTube />} label="آزمایشگاه‌ها" link="/labs-page" onClick={closeMenu} />
    );

    // --- کاربر ---
    if (isLoggedIn) {
      items.push(
        <Divider key="user-div" />,
        <SectionTitle key="user-title" title="حساب کاربری" icon={<Sparkles className="w-4 h-4" />} />,
        <NavItem icon={<User />} label="پروفایل" link="/profile_page" onClick={closeMenu} />,
        <NavItem icon={<ArrowRightLeft />} label="خرید و فروش" link="/trade_page" onClick={closeMenu} />,
        <NavItem icon={<Banknote />} label="واریز / برداشت" link="/deposit-withdraw_page" onClick={closeMenu} />,
        <NavItem icon={<FileText />} label="تراکنش‌ها" link="/transactions_page" onClick={closeMenu} />,
        <NavItem icon={<Package />} label="درخواست آبشده" link="/gold-request_page" onClick={closeMenu} />,
        <NavItem icon={<Banknote />} label="درخواست‌های کیف پول" link="/wallet-requests_page" onClick={closeMenu} />
      );

      // --- پشتیبانی ---
      items.push(
        <Divider key="support-div" />,
        <SectionTitle key="support-title" title="پشتیبانی" icon={<HelpCircle className="w-4 h-4" />} />,
        <NavItem icon={<BookOpen />} label="آموزش" link="/Tutorial" onClick={closeMenu} />,
        <NavItem icon={<HelpCircle />} label="سوالات متداول" link="/FAQ" onClick={closeMenu} />,
        <NavItem icon={<Contact />} label="تماس با ما" link="/Contact" onClick={closeMenu} />,
        <NavItem
          icon={<MessageCircle />}
          label="چت زنده"
          link={isAdmin ? "/admin-chat_page" : "/chat_page"}
          onClick={closeMenu}
          badge={hasNewMessage ? unreadCount : null}
        />

        
      );

      // --- ادمین ---
      if (isAdmin) {
        items.push(
          <Divider key="admin-div" />,
          <SectionTitle key="admin-title" title="مدیریت" icon={<Shield className="w-4 h-4" />} />,
          <NavItem icon={<MessageCircle />} label="چت کاربران" link="/admin-chat_page" onClick={closeMenu} badge={unreadCount > 0 ? unreadCount : null} />
        );
      }
    } else {
      items.push(
        <Divider key="guest-div" />,
        <SectionTitle key="guest-title" title="شروع کنید" />,
        <NavItem icon={<UserPlus />} label="ثبت نام" link="/register_page" onClick={closeMenu} />,
        <NavItem icon={<LogOut className="rotate-180" />} label="ورود" link="/login_page" onClick={closeMenu} />
      );
    }

    return items;
  };

  return (
    <>
      {/* === Header === */}
      <header className="bg-white/95 backdrop-blur-2xl border-b border-gray-100 shadow-lg px-3 md:px-6 py-3 flex justify-between items-center sticky top-0 z-50 font-vazir">
        
        {/* همبرگر */}
        <motion.button
          ref={menuButtonRef}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setOpen(!open)}
          className="text-indigo-600 p-2.5 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 shadow-md hover:shadow-lg transition-all"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </motion.button>

        {/* قیمت طلا */}
        <div className="flex-1 text-center">
          {priceError ? (
            <span className="text-rose-500 text-xs font-medium">{priceError}</span>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 px-3 py-1.5 rounded-full shadow-inner text-xs md:text-sm border border-amber-200"
              dir="ltr"
            >
              <span className="text-amber-700 font-medium">ریال</span>
              <motion.span
                key={displayPrice}
                initial={{ y: -6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400 }}
                className={`${priceColor} font-mono font-bold`}
              >
                {displayPrice != null ? Number(displayPrice).toLocaleString('fa-IR') : '...'}
              </motion.span>
              <span className="text-amber-700">:هر گرم طلا</span>
            </motion.div>
          )}
        </div>

        {/* لوگو + پشتیبانی */}
        <div className="flex flex-col items-end gap-1">
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="ESF Gold" 
              className="h-12 md:h-14 w-auto object-contain drop-shadow-lg transition-transform hover:scale-105"
            />
          </Link>
          {isLoggedIn && (
            <Link
              to={isAdmin ? "/admin-chat_page" : "/chat_page"}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 text-xs font-medium transition-all relative"
            >
              <MessageCircle className="w-4 h-4" />
              <span>پشتیبانی</span>
              {hasNewMessage && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1.5 -left-1.5 min-w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </motion.div>
              )}
            </Link>
          )}
        </div>
      </header>

      {/* === منوی کشویی زیبا === */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            <motion.nav
              ref={menuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-gradient-to-br from-white via-indigo-50/50 to-purple-50/30 backdrop-blur-2xl shadow-2xl z-50 p-5 md:p-6 border-l border-gray-200 overflow-y-auto"
            >
              {/* هدر منو */}
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                <h3 className="text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  منوی اصلی
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setOpen(false)}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </div>

              {/* آیتم‌ها */}
              <div className="space-y-1 flex-1">
                {renderMenuItems()}
              </div>


              {/* فوتر */}
              <div className="text-center text-xs text-gray-500 mt-4 pb-2">
                <p>© ۱۴۰۴ - ESF Gold System</p>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// --- کامپوننت‌ها ---
function NavItem({ icon, label, link, onClick, badge }) {
  return (
    <motion.div whileHover={{ x: -6 }} whileTap={{ scale: 0.98 }}>
      <Link
        to={link}
        onClick={onClick}
        className="flex items-center justify-between p-3.5 rounded-xl hover:bg-white/70 hover:shadow-md transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl group-hover:from-indigo-200 group-hover:to-purple-200 transition-all">
            {icon}
          </div>
          <span className="font-medium text-gray-800 group-hover:text-indigo-700">{label}</span>
        </div>
        {badge !== null && badge > 0 && (
          <div className="bg-rose-500 text-white text-xs font-bold rounded-full min-w-6 h-6 flex items-center justify-center shadow">
            {badge > 99 ? '99+' : badge}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

function SectionTitle({ title, icon }) {
  return (
    <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm mt-5 mb-2 px-3">
      {icon}
      <span>{title}</span>
    </div>
  );
}

function Divider() {
  return <div className="border-t border-gray-200 my-3"></div>;
}